import { create } from 'zustand'
import { teamApi } from '../services/teamApi'
import { userApi } from '../../../services/userApi'

/**
 * Состояние банд (docs/team.md).
 *
 * Карточка банды на фронте не кешируется — каждый заход = новый запрос.
 * Почти все мутации своей банды возвращают свежую карточку целиком, поэтому
 * после них перезапрашивать `/api/team/my/` не нужно: ответ кладётся в `team`.
 *
 * `/api/me/` перезапрашивается там, где меняется членство или баланс игрока
 * (создание, вступление, выход, роспуск, выплата из казны): `team` и
 * `can_create_team` в `me` — источник правды для баннеров и кнопок.
 */

// Защита от гонки при быстрой смене slug и при поиске в каталоге
let teamRequestId = 0
let catalogRequestId = 0
// Статистика и история: период, фильтр по режиму и страница переключаются быстрее ответа
let statsRequestId = 0
let gamesRequestId = 0
let gameRequestId = 0
let topRequestId = 0
let topHistoryRequestId = 0
let captureRequestId = 0
let captureModeRequestId = 0

/** Обновление me: apiService сам синхронизирует user/balance в useAppStore */
const refreshMe = async () => {
  try {
    await userApi.getProfile()
  } catch (e) {
    // молча: карточка банды уже обновлена, баланс подтянется следующим запросом
  }
}

const emptyCatalog = { teams: [], total: 0, page: 1, pages: 1 }
const emptyGames = { games: [], total: 0, page: 1, pages: 1 }

/** Коды ошибок статистики и истории игр в общий вид ошибки экрана */
const statsErrorFrom = (status, data) => {
  if (status === 403) return 'stats_hidden'
  if (status === 429) return 'rate_limited'
  if (status === 404) return data?.error === 'game_not_found' ? 'game_not_found' : 'not_found'
  return data?.error || 'load_error'
}

export const useTeamStore = create((set, get) => ({
  // Открытая карточка банды (своя или чужая — формат одинаковый)
  team: null,
  isTeamLoading: false,
  // 'not_found' (404, в том числе распущенная банда) | 'load_error' (500 / сеть) | null
  teamError: null,

  // Каталог
  catalog: emptyCatalog,
  isCatalogLoading: false,
  catalogError: null,
  recentLeft: [],
  recentLeftReceivedAt: 0,

  // Мои заявки
  myApplications: [],
  isApplicationsLoading: false,

  top: null,
  isTopLoading: false,
  topError: null,
  topHistory: { days: [] },
  isTopHistoryLoading: false,
  topHistoryError: null,
  capture: null,
  isCaptureLoading: false,
  captureError: null,
  captureMode: null,
  isCaptureModeLoading: false,
  captureModeError: null,

  // Казна и аудит
  treasury: { operations: [], total: 0, page: 1, receivedAt: 0 },
  isTreasuryLoading: false,
  audit: { events: [], page: 1 },
  isAuditLoading: false,

  // Статистика командных игр и их история (docs/team-stats-api.md)
  stats: null,
  isStatsLoading: false,
  statsError: null,
  games: emptyGames,
  isGamesLoading: false,
  gamesError: null,
  game: null,
  isGameLoading: false,
  gameError: null,

  // Флаги операций
  isMutating: false,
  isVotePending: false,
  isUploadingImage: false,
  isSavingSettings: false,

  /**
   * Карточка банды. slug === 'my' / null — своя банда (GET /api/team/my/),
   * там же `team: null`, если игрок ни в какой банде не состоит.
   */
  loadTeam: async (slug) => {
    const requestId = ++teamRequestId
    set({ isTeamLoading: true, teamError: null, team: null })

    const isMy = !slug || slug === 'my'
    const { ok, status, data } = isMy ? await teamApi.getMyTeam() : await teamApi.getTeam(slug)

    if (requestId !== teamRequestId) return

    if (status === 404) {
      set({ teamError: 'not_found', isTeamLoading: false })
      return
    }

    if (!ok) {
      set({ teamError: status === 429 ? 'rate_limited' : 'load_error', isTeamLoading: false })
      return
    }

    // /api/team/my/ отдаёт team: null — это не ошибка, а «банды нет»
    set({ team: data?.team ?? null, isTeamLoading: false,
      ...(isMy ? { recentLeft: Array.isArray(data?.recent_left) ? data.recent_left : [], recentLeftReceivedAt: Date.now() } : {}) })
  },

  /**
   * Каталог банд: без q — только набирающие, с q (от 2 символов) — все живые.
   * Фильтры (level / modes / age / activity / free / sort) просто пробрасываются
   * в запрос — собирает и хранит их экран каталога.
   */
  loadCatalog: async ({ q = '', page = 1, ...filters } = {}) => {
    const requestId = ++catalogRequestId
    set({ isCatalogLoading: true, catalogError: null })

    const { ok, data } = await teamApi.getCatalog({ q: q || undefined, page, ...filters })

    if (requestId !== catalogRequestId) return

    if (!ok || !Array.isArray(data?.teams)) {
      // invalid_filter — ошибка в коде фронта, а не действие игрока
      if (data?.error === 'invalid_filter') {
        console.error('Каталог банд: неизвестное значение фильтра', filters)
      }
      set({ catalogError: data?.error || 'load_error', isCatalogLoading: false })
      return
    }

    set({
      catalog: {
        teams: data.teams,
        total: data.total ?? data.teams.length,
        page: data.page ?? page,
        pages: data.pages ?? 1
      },
      isCatalogLoading: false,
      recentLeft: Array.isArray(data.recent_left) ? data.recent_left : [],
      recentLeftReceivedAt: Date.now()
    })
  },

  loadCapture: async () => {
    const requestId = ++captureRequestId
    set({ isCaptureLoading: true, captureError: null })
    const { ok, data } = await teamApi.getCapture()
    if (requestId !== captureRequestId) return
    if (!ok || typeof data?.enabled !== 'boolean') {
      set({ captureError: data?.error || 'load_error', isCaptureLoading: false })
      return
    }
    set({ capture: { ...data, receivedAt: Date.now() }, isCaptureLoading: false })
  },

  loadCaptureMode: async (mode) => {
    const requestId = ++captureModeRequestId
    set({ isCaptureModeLoading: true, captureModeError: null })
    const { ok, data } = await teamApi.getCaptureMode(mode)
    if (requestId !== captureModeRequestId) return
    if (!ok || typeof data?.enabled !== 'boolean') {
      set({ captureModeError: data?.error || 'load_error', isCaptureModeLoading: false })
      return
    }
    set({ captureMode: { ...data, receivedAt: Date.now() }, isCaptureModeLoading: false })
  },

  resetCaptureMode: () => {
    ++captureModeRequestId
    set({ captureMode: null, captureModeError: null, isCaptureModeLoading: false })
  },

  loadTop: async () => {
    const requestId = ++topRequestId
    set({ isTopLoading: true, topError: null })
    const { ok, data } = await teamApi.getTop()
    if (requestId !== topRequestId) return
    if (!ok || typeof data?.enabled !== 'boolean') {
      set({ topError: data?.error || 'load_error', isTopLoading: false })
      return
    }
    set({ top: data, isTopLoading: false })
  },

  loadTopHistory: async (mode) => {
    const requestId = ++topHistoryRequestId
    set({ isTopHistoryLoading: true, topHistoryError: null })
    const { ok, data } = await teamApi.getTopHistory(mode)
    if (requestId !== topHistoryRequestId) return
    if (!ok || !Array.isArray(data?.days)) {
      set({ topHistory: { days: [] }, topHistoryError: data?.error || 'load_error', isTopHistoryLoading: false })
      return
    }
    set({ topHistory: data, isTopHistoryLoading: false })
  },

  /** Создание банды. Ответ 200 — карточка новой банды, членство меняется → обновляем me */
  createTeam: async (name) => {
    if (get().isMutating) return null
    set({ isMutating: true })

    const { ok, status, data } = await teamApi.create(name)
    set({ isMutating: false })

    if (ok && data?.team) {
      set({ team: data.team })
      await refreshMe()
      return { ok: true, team: data.team }
    }
    return { ok: false, status, error: data?.error, retry_after: data?.retry_after }
  },

  /** Заявка на вступление */
  apply: async (slug, text) => {
    if (get().isMutating) return null
    set({ isMutating: true })

    const { ok, status, data } = await teamApi.apply(slug, text)
    set({ isMutating: false })

    if (ok) return { ok: true }
    return { ok: false, status, error: data?.error, retry_after: data?.retry_after }
  },

  loadMyApplications: async () => {
    set({ isApplicationsLoading: true })
    const { ok, data } = await teamApi.getMyApplications()
    set({
      myApplications: ok && Array.isArray(data?.applications) ? data.applications : [],
      isApplicationsLoading: false
    })
  },

  /** Отозвать заявку; id === null — сразу все */
  cancelApplication: async (id) => {
    const { ok, status, data } = await teamApi.cancelApplication(id)
    if (ok) {
      await get().loadMyApplications()
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /**
   * Решение по входящей заявке (модератор и выше). Ответ 200 — свежая карточка
   * своей банды: списки состава и заявок перезапрашивать не нужно.
   */
  decideApplication: async (id, approve) => {
    if (get().isMutating) return null
    set({ isMutating: true })

    const { ok, status, data } = approve
      ? await teamApi.approveApplication(id)
      : await teamApi.rejectApplication(id)
    set({ isMutating: false })

    if (ok && data?.team) {
      set({ team: data.team })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /** Смена роли участника; ответ — свежая карточка своей банды */
  setMemberRole: async (chatId, role) => {
    if (get().isMutating) return null
    set({ isMutating: true })

    const { ok, status, data } = await teamApi.setRole(chatId, role)
    set({ isMutating: false })

    if (ok && data?.team) {
      set({ team: data.team })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  kickMember: async (chatId) => {
    if (get().isMutating) return null
    set({ isMutating: true })

    const { ok, status, data } = await teamApi.kick(chatId)
    set({ isMutating: false })

    if (ok && data?.team) {
      set({ team: data.team })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /** Выход из банды: ответ {result: true} — дальше me обновляет членство */
  leaveTeam: async () => {
    if (get().isMutating) return null
    set({ isMutating: true })

    const { ok, status, data } = await teamApi.leave()
    set({ isMutating: false })

    if (ok && data?.result) {
      set({ team: null })
      await refreshMe()
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /** Передача создателя: согласия принимающего не спрашивают, бывший создатель — зам */
  transferOwner: async (chatId) => {
    if (get().isMutating) return null
    set({ isMutating: true })

    const { ok, status, data } = await teamApi.transfer(chatId)
    set({ isMutating: false })

    if (ok) {
      if (data?.team) set({ team: data.team })
      await refreshMe()
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /** Выплата из казны: кристаллы приходят получателю сразу → обновляем me */
  payFromTreasury: async (chatId, amount) => {
    if (get().isMutating) return null
    set({ isMutating: true })

    const { ok, status, data } = await teamApi.pay(chatId, amount)
    set({ isMutating: false })

    if (ok) {
      if (data?.team) set({ team: data.team })
      await Promise.all([get().loadTreasury(1), refreshMe()])
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  loadTreasury: async (page = 1) => {
    set({ isTreasuryLoading: true })
    const { ok, data } = await teamApi.getTreasuryHistory(page)
    set({
      treasury: {
        operations: ok && Array.isArray(data?.operations) ? data.operations : [],
        total: data?.total ?? 0,
        page: data?.page ?? page,
        receivedAt: Date.now()
      },
      isTreasuryLoading: false
    })
  },

  loadAudit: async (page = 1) => {
    set({ isAuditLoading: true })
    const { ok, data } = await teamApi.getAudit(page)
    set({
      audit: {
        events: ok && Array.isArray(data?.events) ? data.events : [],
        page: data?.page ?? page
      },
      isAuditLoading: false
    })
  },

  /**
   * Статистика командных игр (docs/team-stats-api.md, §3). period — 'today' | 'yesterday' | '7' | '30' | 'all'.
   * 403 stats_hidden — банда закрыла статистику от чужих (`can_view_stats: false`
   * в карточке; кнопку в таком случае не показываем вовсе, но экран по прямой
   * ссылке всё равно должен объяснить, почему пусто).
   */
  loadStats: async (slug, period = 'all') => {
    const requestId = ++statsRequestId
    set({ isStatsLoading: true, statsError: null })

    const { ok, status, data } = await teamApi.getStats(slug, period)
    if (requestId !== statsRequestId) return

    if (!ok) {
      set({ stats: null, statsError: statsErrorFrom(status, data), isStatsLoading: false })
      return
    }
    set({ stats: data ?? null, isStatsLoading: false })
  },

  /** История командных игр: 20 на страницу, kind — необязательный фильтр по режиму */
  loadGames: async (slug, { page = 1, kind } = {}) => {
    const requestId = ++gamesRequestId
    set({ isGamesLoading: true, gamesError: null })

    const { ok, status, data } = await teamApi.getGames(slug, { page, kind })
    if (requestId !== gamesRequestId) return

    if (!ok || !Array.isArray(data?.games)) {
      set({ games: emptyGames, gamesError: statsErrorFrom(status, data), isGamesLoading: false })
      return
    }

    set({
      games: {
        games: data.games,
        total: data.total ?? data.games.length,
        page: data.page ?? page,
        pages: data.pages ?? 1
      },
      isGamesLoading: false
    })
  },

  /** Одна командная игра: у сессии в ответе `items`, у раунда — `null` (ведём по `ref`) */
  loadGame: async (slug, id) => {
    const requestId = ++gameRequestId
    set({ isGameLoading: true, gameError: null, game: null })

    const { ok, status, data } = await teamApi.getGame(slug, id)
    if (requestId !== gameRequestId) return

    if (!ok || !data?.game) {
      set({ gameError: statsErrorFrom(status, data), isGameLoading: false })
      return
    }
    set({ game: data.game, isGameLoading: false })
  },

  resetStats: () => {
    ++statsRequestId
    set({ stats: null, statsError: null, isStatsLoading: false })
  },
  resetGames: () => {
    ++gamesRequestId
    set({ games: emptyGames, gamesError: null, isGamesLoading: false })
  },
  resetGame: () => {
    ++gameRequestId
    set({ game: null, gameError: null, isGameLoading: false })
  },

  /** Настройки банды (частичное обновление). Ответ 200 — свежая карточка */
  saveSettings: async (payload) => {
    set({ isSavingSettings: true })
    const { ok, status, data } = await teamApi.saveSettings(payload)
    set({ isSavingSettings: false })

    if (ok && data?.team) {
      set({ team: data.team })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error, retry_after: data?.retry_after }
  },

  /** Объявление лидера (только создатель) */
  saveAnnouncement: async (text) => {
    set({ isSavingSettings: true })
    const { ok, status, data } = await teamApi.setAnnouncement(text)
    set({ isSavingSettings: false })

    if (ok && data?.team) {
      set({ team: data.team })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  saveColors: async (colors) => {
    set({ isSavingSettings: true })
    const { ok, status, data } = await teamApi.saveColors(colors)
    set({ isSavingSettings: false })

    if (ok && data?.team) {
      set({ team: data.team })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  uploadAvatar: async (file) => {
    if (get().isUploadingImage) return null
    set({ isUploadingImage: true })

    const { ok, status, data } = await teamApi.uploadAvatar(file)
    set({ isUploadingImage: false })

    if (ok && data?.team) {
      set({ team: data.team })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  deleteAvatar: async () => {
    set({ isSavingSettings: true })
    const { ok, status, data } = await teamApi.deleteAvatar()
    set({ isSavingSettings: false })

    if (ok && data?.team) {
      set({ team: data.team })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /** Фон карточки банды: возвращаем свежий background для синхронизации редактора */
  uploadBackground: async (file, settings) => {
    if (get().isUploadingImage) return null
    set({ isUploadingImage: true })

    const { ok, status, data } = await teamApi.uploadBackground(file, settings)
    set({ isUploadingImage: false })

    if (ok && data?.team) {
      set({ team: data.team })
      return { ok: true, background: data.team.background ?? null }
    }
    return { ok: false, status, error: data?.error }
  },

  saveBackgroundSettings: async (settings) => {
    set({ isSavingSettings: true })
    const { ok, status, data } = await teamApi.saveBackgroundSettings(settings)
    set({ isSavingSettings: false })

    if (ok && data?.team) {
      set({ team: data.team })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  deleteBackground: async () => {
    set({ isSavingSettings: true })
    const { ok, status, data } = await teamApi.deleteBackground()
    set({ isSavingSettings: false })

    if (ok && data?.team) {
      set({ team: data.team })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /**
   * Голос за банду: 1 — лайк, -1 — дизлайк, 0 — снять.
   * Ответ — только счётчики, карточку не перезапрашиваем.
   */
  setVote: async (slug, value) => {
    if (get().isVotePending) return null
    set({ isVotePending: true })

    const { ok, status, data } = await teamApi.setVote(slug, value)
    set({ isVotePending: false })

    if (ok && typeof data?.likes === 'number') {
      const team = get().team
      if (team) {
        set({
          team: {
            ...team,
            likes: data.likes,
            dislikes: data.dislikes,
            my_vote: data.my_vote
          }
        })
      }
      set({ recentLeft: get().recentLeft.map((entry) => entry.team?.slug === slug ? { ...entry, my_vote: data.my_vote } : entry) })
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  /**
   * Prepared message «Пригласить в банду»; id живёт ограниченное время —
   * не кешируем, запрашиваем новый на каждое нажатие.
   */
  shareTeam: async () => {
    const { ok, status, data } = await teamApi.share()
    return { ok, status, data, error: data?.error }
  },

  /**
   * Роспуск банды — только создатель. У одного и того же запроса две концовки,
   * решает их бэкенд: `disbanded: true` — пустая банда распущена мгновенно
   * (`team: null`, кулдауна на создание новой нет), `false` — запущен таймер на
   * 6 часов и вернулась живая карточка. На `delete_instant`, полученный при
   * отрисовке экрана, опираться нельзя: состав мог измениться до нажатия.
   */
  startDelete: async () => {
    if (get().isMutating) return null
    set({ isMutating: true })

    const { ok, status, data } = await teamApi.deleteTeam()
    set({ isMutating: false })

    if (ok) {
      if (data?.disbanded) {
        set({ team: null })
        await refreshMe()
        return { ok: true, disbanded: true }
      }
      if (data?.team) set({ team: data.team })
      else await get().loadTeam('my')
      await refreshMe()
      return { ok: true, disbanded: false }
    }
    return { ok: false, status, error: data?.error }
  },

  cancelDelete: async () => {
    if (get().isMutating) return null
    set({ isMutating: true })

    const { ok, status, data } = await teamApi.cancelDelete()
    set({ isMutating: false })

    if (ok) {
      if (data?.team) set({ team: data.team })
      else await get().loadTeam('my')
      await refreshMe()
      return { ok: true }
    }
    return { ok: false, status, error: data?.error }
  },

  resetTeam: () => {
    ++teamRequestId
    set({ team: null, teamError: null, isTeamLoading: false })
  },

  reset: () => {
    ++teamRequestId
    ++catalogRequestId
    ++statsRequestId
    ++gamesRequestId
    ++gameRequestId
    ++topRequestId
    ++topHistoryRequestId
    ++captureRequestId
    ++captureModeRequestId
    set({
      team: null,
      teamError: null,
      isTeamLoading: false,
      catalog: emptyCatalog,
      catalogError: null,
      isCatalogLoading: false,
      recentLeft: [],
      recentLeftReceivedAt: 0,
      myApplications: [],
      top: null,
      isTopLoading: false,
      topError: null,
      topHistory: { days: [] },
      topHistoryError: null,
      isTopHistoryLoading: false,
      capture: null,
      isCaptureLoading: false,
      captureError: null,
      captureMode: null,
      isCaptureModeLoading: false,
      captureModeError: null,
      treasury: { operations: [], total: 0, page: 1, receivedAt: 0 },
      audit: { events: [], page: 1 },
      stats: null,
      isStatsLoading: false,
      statsError: null,
      games: emptyGames,
      isGamesLoading: false,
      gamesError: null,
      game: null,
      isGameLoading: false,
      gameError: null,
      isMutating: false,
      isVotePending: false,
      isUploadingImage: false,
      isSavingSettings: false
    })
  }
}))

export default useTeamStore
